/**
 * createRole — RPC contract for the createRole callable.
 *
 * `role.entity.ts` marks `create` `.requiresHandler('createRole')` — the
 * Firestore rule it replaces had NO subset guard at all (a `roles.manage`
 * holder could mint a role carrying every catalog token, including
 * `roles.manage` and `members.invite` themselves); `createRole.api.ts`
 * (onCall) closes that gap: caller holds `tickets.roles.manage` → the
 * proposed permissions ⊆ caller's own role's permissions → mint a fresh role
 * doc, always `isBuiltIn: false` (server-stamped, never a client-supplied
 * field — the built-in `owner` doc can never be reached through this write path).
 *
 * Fields justified:
 *   name        — display string for the role admin surface.
 *   permissions — the tokens the new role grants; must be ⊆ the caller's
 *                 own role's permissions.
 *
 * Output:
 *   roleId — the generated role doc id (Admin-SDK auto-id), returned so the
 *            FE can select the just-created role without a second read.
 *
 * Excluded:
 *   isBuiltIn — never client-supplied; the handler always writes `false`.
 */
import { z } from "zod";
import { PermissionSchema } from "./permissions.ts";
import { Id } from "./workspace.entity.ts";

export const CreateRoleInput = z
	.object({
		orgId: Id,
		name: z.string().min(1),
		permissions: z
			.array(PermissionSchema)
			.min(1)
			.describe("must be ⊆ the caller's own role's permissions"),
	})
	.strict();
export type CreateRoleInput = z.infer<typeof CreateRoleInput>;

export const CreateRoleOutput = z
	.object({
		roleId: z.string().min(1).describe("the generated role doc id"),
	})
	.strict();
export type CreateRoleOutput = z.infer<typeof CreateRoleOutput>;
